/* @jsxImportSource @emotion/react */

import { useCallback, useEffect, useRef, useState } from 'react'
import { css } from '@emotion/react'
import { motion, useAnimationControls, useMotionValue } from 'motion/react'
import { FormattedMessage } from 'react-intl'

import ComplianceLogos from '../../../assets/compliance-logos.svg'
import { Button } from '../../../components/Button/Button'
import { CustomerLogosWall } from '../../../components/CustomerLogosWall'
import { Section } from '../../../components/Section'
import { WebsiteText } from '../../../components/WebsiteText'
import { useRoutes } from '../../../routes'
import { MEDIUM_BREAKPOINT, SMALL_BREAKPOINT } from '../../../utils/breakpoints'
import { desktopOnlyCss } from '../../../utils/responsive'
import { DemoAgent, type DemoAgentHandle } from './DemoAgent'
import {
  DemoKnowledgeBase,
  type DemoKnowledgeBaseHandle,
} from './DemoKnowledgeBase'
import { MobileHeroText } from './MobileHeroText'
import { ScenarioButtons } from './ScenarioButtons'
import { SCENARIOS } from './scenarios'
import {
  FINAL_HOLD_MS,
  runScenario,
  scenarioDurationMs,
} from './storyController'

const DEFAULT_CURSOR_COLOR = 'var(--shade-00)'

// Yellow needs dark text to stay legible; everything else pairs with white.
const isLightColor = (color: string) => color === 'var(--yellow)'

const ReviewerCursor = ({
  name,
  color = DEFAULT_CURSOR_COLOR,
  animateLabel = false,
}: {
  name: string
  color?: string
  // When true, the label scales/fades in on mount — used by the "You" cursor.
  animateLabel?: boolean
}) => {
  const textColor = isLightColor(color) ? 'var(--shade-00)' : '#fff'
  const labelStyle: React.CSSProperties = {
    position: 'absolute',
    left: 18,
    top: 16,
    background: color,
    color: textColor,
    borderRadius: 8,
    padding: '2px 8px',
    fontSize: 12,
    whiteSpace: 'nowrap',
    boxShadow: '0 2px 6px rgba(0, 0, 0, 0.2)',
    transformOrigin: 'top left',
  }
  return (
    <div style={{ position: 'relative' }}>
      <svg
        width="24"
        height="24"
        viewBox="0 0 24 24"
        fill="none"
        aria-hidden
        style={{
          display: 'block',
          filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.25))',
        }}
      >
        <path
          d="M5 3l13 7.4-5.4 1.3-2.7 5L5 3z"
          fill={color}
          stroke="#fff"
          strokeWidth="1.5"
          strokeLinejoin="round"
        />
      </svg>
      {animateLabel ? (
        <motion.div
          className="font-weight-semibold"
          style={labelStyle}
          initial={{ opacity: 0, scale: 0.7, x: -6 }}
          animate={{ opacity: 1, scale: 1, x: 0 }}
          transition={{
            duration: 0.22,
            ease: [0.34, 1.56, 0.64, 1],
            delay: 0.05,
          }}
        >
          {name}
        </motion.div>
      ) : (
        <div className="font-weight-semibold" style={labelStyle}>
          {name}
        </div>
      )}
    </div>
  )
}

const sleep = (ms: number) =>
  new Promise<void>((resolve) => setTimeout(resolve, ms))

export function SectionHero() {
  const routes = useRoutes()
  const kbRef = useRef<DemoKnowledgeBaseHandle>(null)
  const agentRef = useRef<DemoAgentHandle>(null)
  const stageWrapRef = useRef<HTMLDivElement>(null)
  const cursorControls = useAnimationControls()

  const [activeIdx, setActiveIdx] = useState(0)
  const [playId, setPlayId] = useState(0)
  const [runKey, setRunKey] = useState(0)
  const startIdxRef = useRef(0)
  // Position lives in motion values, not React state — every mousemove writes
  // straight to `transform` via motion, skipping React re-renders entirely.
  // Only the show/hide flag goes through state.
  const userCursorX = useMotionValue(0)
  const userCursorY = useMotionValue(0)
  const [userCursorVisible, setUserCursorVisible] = useState(false)
  // Remember the last viewport-relative mouse coords so we can recompute the
  // stage-relative position on scroll (the mouse hasn't moved but the stage's
  // top/left has, so the offset into the stage changes).
  const lastClientXRef = useRef(0)
  const lastClientYRef = useRef(0)

  // Stable across renders (motion values from useMotionValue are ref-stable),
  // so the scroll-listener effect below doesn't re-attach on every render.
  const updateUserCursorFromClient = useCallback(
    (clientX: number, clientY: number) => {
      const rect = stageWrapRef.current?.getBoundingClientRect()
      if (!rect) return
      userCursorX.set(clientX - rect.left)
      userCursorY.set(clientY - rect.top)
    },
    [userCursorX, userCursorY]
  )

  // Bumped on every mouseenter so the inner ReviewerCursor remounts and its
  // label replays the scale-in animation — without unmounting the fade wrapper.
  const [userCursorEnterId, setUserCursorEnterId] = useState(0)

  // While visible (and during fade-out), we track the mouse at window level so
  // the cursor keeps following the native mouse after it leaves the stage —
  // making the exit feel like a fade-in-place transformation instead of the
  // "You" cursor freezing at the border while the OS cursor walks away.
  const trackingListenersRef = useRef<{
    move: (e: MouseEvent) => void
    scroll: () => void
  } | null>(null)
  const fadeOutTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  // Matches the fade transition duration below + a small tail for the easing.
  const USER_CURSOR_FADE_OUT_MS = 220

  const startTracking = useCallback(() => {
    if (trackingListenersRef.current) return
    const move = (e: MouseEvent) => {
      lastClientXRef.current = e.clientX
      lastClientYRef.current = e.clientY
      updateUserCursorFromClient(e.clientX, e.clientY)
    }
    const scroll = () => {
      updateUserCursorFromClient(lastClientXRef.current, lastClientYRef.current)
    }
    window.addEventListener('mousemove', move, { passive: true })
    window.addEventListener('scroll', scroll, { passive: true })
    trackingListenersRef.current = { move, scroll }
  }, [updateUserCursorFromClient])

  const stopTracking = () => {
    const t = trackingListenersRef.current
    if (!t) return
    window.removeEventListener('mousemove', t.move)
    window.removeEventListener('scroll', t.scroll)
    trackingListenersRef.current = null
  }

  const handleStageMouseEnter = (e: React.MouseEvent<HTMLDivElement>) => {
    if (fadeOutTimerRef.current !== null) {
      clearTimeout(fadeOutTimerRef.current)
      fadeOutTimerRef.current = null
    }
    lastClientXRef.current = e.clientX
    lastClientYRef.current = e.clientY
    updateUserCursorFromClient(e.clientX, e.clientY)
    startTracking()
    setUserCursorEnterId((id) => id + 1)
    setUserCursorVisible(true)
  }
  const handleStageMouseLeave = () => {
    setUserCursorVisible(false)
    // Keep position tracking through the fade so the "You" cursor follows your
    // real cursor off-stage rather than freezing at the boundary.
    fadeOutTimerRef.current = setTimeout(() => {
      stopTracking()
      fadeOutTimerRef.current = null
    }, USER_CURSOR_FADE_OUT_MS)
  }

  useEffect(() => {
    // Cleanup on unmount: tear down any active tracking + pending fadeout timer.
    return () => {
      if (fadeOutTimerRef.current !== null) {
        clearTimeout(fadeOutTimerRef.current)
      }
      const t = trackingListenersRef.current
      if (t) {
        window.removeEventListener('mousemove', t.move)
        window.removeEventListener('scroll', t.scroll)
        trackingListenersRef.current = null
      }
    }
  }, [])

  useEffect(() => {
    void runKey
    let cancelled = false
    let idx = startIdxRef.current

    const loop = async () => {
      const ctx = {
        agent: agentRef.current,
        kb: kbRef.current,
        cursor: cursorControls,
        stage: stageWrapRef.current,
        cancelled: () => cancelled,
      }
      ctx.agent?.hide()
      ctx.agent?.reset()
      ctx.kb?.reset()

      while (!cancelled) {
        setActiveIdx(idx)
        setPlayId((p) => p + 1)
        await runScenario(SCENARIOS[idx], ctx)
        if (cancelled) return
        await sleep(FINAL_HOLD_MS)
        if (cancelled) return
        ctx.agent?.reset()
        ctx.kb?.reset()
        idx = (idx + 1) % SCENARIOS.length
      }
    }
    void loop()
    return () => {
      cancelled = true
    }
  }, [runKey, cursorControls])

  const jumpToScenario = (i: number) => {
    startIdxRef.current = i
    setActiveIdx(i)
    setRunKey((key) => key + 1)
  }

  return (
    <Section background="moon-dust-lighter" containerVerticalPadding={80}>
      <div
        css={css`
            && {
              @media (max-width: ${SMALL_BREAKPOINT - 1}px) {
                display: none;
              }
            }
          `}
      >
        <div className="position-relative margin-bottom-24">
          <WebsiteText
            variant="heroTitle"
            className="text-align-center position-relative"
            css={css`
            && {
              font-size: 56px;
              max-width: 900px;
              margin-left: auto;
              margin-right: auto;
            }
          `}
          >
            <FormattedMessage
              id="new.hero.title"
              defaultMessage="A source of truth for your team.{br}And the AI they work with."
              values={{ br: <br /> }}
            />
          </WebsiteText>
          {/* Mobile-only title — delegates to MobileHeroText. Wrapper handles
            the show/hide so MobileHeroText stays a pure visual component. */}
        </div>

        <WebsiteText
          variant="body-lg"
          className="position-relative margin-left-auto margin-right-auto text-align-center margin-bottom-24"
          style={{ maxWidth: 720, zIndex: 0 }}
        >
          <FormattedMessage
            id="new.hero.subtitle"
            defaultMessage="Slite connects to the tools where work happens to build and maintain the knowledge your business depends on, so your team and their AI can work from reliable answers."
          />
        </WebsiteText>

        <div className="width-full margin-bottom-60 flexbox flexbox-centered flexbox-gap-24 flexbox-wrap">
          <Button
            size="huge"
            variant="primary-brand"
            href={routes.bookDemo()}
            style={{
              height: 64,
              background: 'var(--saturn-orange)',
              color: 'white',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: '0 32px',
              fontWeight: 600,
              fontSize: 17,
            }}
          >
            <FormattedMessage id="new.hero.cta" defaultMessage="Talk to us" />
          </Button>
          <img
            // eslint-disable-next-line formatjs/no-literal-string-in-jsx
            alt="AICPA SOC 2 logo"
            src={ComplianceLogos}
            style={{ maxWidth: 200 }}
          />
        </div>
      </div>

      {/* MobileHeroText only renders below SMALL_BREAKPOINT; the wrapper
          owns the show/hide so the inner component stays purely visual. */}
      <div
        css={css`
          display: none;
          @media (max-width: ${SMALL_BREAKPOINT - 1}px) {
            display: block;
            height: calc(100vh - 64px - 120px);
            max-height: 600px;
          }
        `}
      >
        <MobileHeroText />
      </div>

      {/* hero animation */}

      <div
        ref={stageWrapRef}
        className="position-relative margin-left-auto margin-right-auto"
        onMouseEnter={handleStageMouseEnter}
        onMouseLeave={handleStageMouseLeave}
        css={css`
          @media (max-width: ${MEDIUM_BREAKPOINT - 1}px) {
            overflow: hidden;
          }
        `}
        style={{
          isolation: 'isolate',
          maxWidth: 900,
          cursor: userCursorVisible ? 'none' : undefined,
        }}
      >
        <div
          className="radius-larger flexbox flexbox-centered shade-30 position-relative"
          css={css`
            aspect-ratio: 16 / 9;
            @media (max-width: ${SMALL_BREAKPOINT - 1}px) {
              aspect-ratio: 2 / 3;
            }
          `}
          style={{ zIndex: 2 }}
        >
          <DemoKnowledgeBase
            ref={kbRef}
            initialDocId={SCENARIOS[0].initialDocId}
          />
        </div>

        {/* Agent composer.
            Desktop: sticky at the bottom of the stage with a y-offset that
            lets the card poke past the KB's bottom edge (original design).
            Mobile: tucked inside the KB at the bottom — tighter max-width
            and a small bottom inset so the card sits within the KB. */}
        <div
          className="flexbox flexbox-justify-center flexbox-align-end"
          css={css`
            position: absolute;
            inset: 0;
            pointer-events: none;
            z-index: 3;
          `}
        >
          <div
            css={css`
              position: sticky;
              bottom: 34px;
              width: 100%;
              max-width: 510px;
              transform: translateY(24px);
              @media (max-width: ${SMALL_BREAKPOINT - 1}px) {
                position: static;
                bottom: auto;
                transform: none;
                max-width: calc(100% - 24px);
                padding-bottom: 12px;
              }
            `}
          >
            <DemoAgent ref={agentRef} />
          </div>
        </div>

        <motion.div
          className="position-absolute"
          css={desktopOnlyCss}
          style={{
            left: 0,
            top: 0,
            zIndex: 4,
            opacity: 0,
            pointerEvents: 'none',
          }}
          animate={cursorControls}
        >
          <ReviewerCursor
            name={SCENARIOS[activeIdx].reviewer.name}
            color={SCENARIOS[activeIdx].reviewer.cursorColor}
          />
        </motion.div>

        {/*
          Outer div owns the position (motion values write straight to transform
          — no React renders per frame). Inner div owns the fade/scale show-hide
          animation, with a 3px y-offset so the cursor settles into place on
          enter and drifts on exit instead of snapping at the mouse point.
        */}
        <motion.div
          className="position-absolute"
          css={desktopOnlyCss}
          style={{
            top: 0,
            left: 0,
            x: userCursorX,
            y: userCursorY,
            zIndex: 5,
            pointerEvents: 'none',
            willChange: 'transform',
          }}
        >
          {/*
            Fade wrapper stays mounted forever — animating the `animate` prop
            cross-fades smoothly between visible/hidden. The inner ReviewerCursor
            is keyed on the enter counter so its label scale-in replays every
            time the user re-enters, without remounting the fade wrapper itself
            (which would cut the fade-out short and look chopped).
          */}
          <motion.div
            initial={{ opacity: 0, scale: 0.85, y: 3 }}
            animate={{
              opacity: userCursorVisible ? 1 : 0,
              scale: userCursorVisible ? 1 : 0.85,
              y: userCursorVisible ? 0 : 3,
            }}
            transition={{ duration: 0.18, ease: [0.4, 0, 0.2, 1] }}
            style={{ willChange: 'transform, opacity' }}
          >
            <ReviewerCursor
              key={userCursorEnterId}
              name="You"
              color="var(--shade-00)"
              animateLabel
            />
          </motion.div>
        </motion.div>
      </div>
      {/* Scenario switcher is desktop-only — on mobile the demo cycles
          on its own and the row of pills crowds the small viewport. */}
      <div
        className="margin-top-60"
        css={css`
          margin-bottom: 100px;
          @media (max-width: ${SMALL_BREAKPOINT - 1}px) {
            display: none;
          }
        `}
      >
        <ScenarioButtons
          scenarios={SCENARIOS}
          activeIdx={activeIdx}
          playId={playId}
          durationMs={scenarioDurationMs()}
          onSelect={jumpToScenario}
        />
      </div>
      <div
        css={css`
          @media (max-width: ${SMALL_BREAKPOINT - 1}px) {
            padding-top: 48px;
          }
        `}
      >
        <CustomerLogosWall />
      </div>
    </Section>
  )
}
